import { useEffect, useState } from 'react'
import { apiService, type TenantAgent, type UserAgentNameEntry } from '@/service/api'
import { useAppStore } from '@/store'
import { toast } from 'sonner'
import { Loader2, Users, X, Lock } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { IntuiBadge } from '@/components/intueri/IntuiBadge'
import { Switch } from '@/components/ui/switch'

export default function AgentsV2() {
  const { t } = useTranslation()
  const { user } = useAppStore()
  const isAdmin = user?.role === 'admin'
  const [agents, setAgents] = useState<TenantAgent[]>([])
  const [nicknamesByAgentId, setNicknamesByAgentId] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [togglingAgents, setTogglingAgents] = useState<Set<string>>(new Set())
  const [manageAgent, setManageAgent] = useState<TenantAgent | null>(null)
  const [nicknameInput, setNicknameInput] = useState('')

  useEffect(() => { loadAgents() }, [])

  const loadAgents = async () => {
    try {
      setLoading(true)
      const response = await apiService.getTenantAgents()
      setAgents(response.agents)
    } catch (err) {
      console.error('Failed to load tenant agents:', err)
      toast.error(t('common:settings.agent_management.load_error'))
    } finally {
      setLoading(false)
    }
    try {
      const namesRes = await apiService.getUserAgentNames()
      const map: Record<string, string> = {}
      namesRes.forEach((e: UserAgentNameEntry) => { map[e.agentId] = e.customName })
      setNicknamesByAgentId(map)
    } catch (err) {
      console.error('Failed to load agent nicknames:', err)
    }
  }

  const handleToggle = async (agentId: string, enabled: boolean) => {
    setTogglingAgents((prev) => new Set(prev).add(agentId))
    try {
      const updated = await apiService.updateTenantAgent(agentId, enabled)
      setAgents((prev) => prev.map((a) => a.id === agentId ? { ...a, isEnabled: updated.isEnabled } : a))
      toast.success(
        enabled
          ? t('common:settings.agent_management.enabled_toast', { name: updated.name })
          : t('common:settings.agent_management.disabled_toast', { name: updated.name })
      )
    } catch (err) {
      console.error(`Failed to toggle agent ${agentId}:`, err)
      toast.error(t('common:settings.agent_management.update_error'))
    } finally {
      setTogglingAgents((prev) => { const n = new Set(prev); n.delete(agentId); return n })
    }
  }

  const handleManageOpen = (agent: TenantAgent) => {
    setManageAgent(agent)
    setNicknameInput(nicknamesByAgentId[agent.id] || '')
  }

  const handleSaveNickname = async () => {
    if (!manageAgent) return
    try {
      await apiService.upsertUserAgentName(manageAgent.id, nicknameInput.trim())
      setNicknamesByAgentId((prev) => ({ ...prev, [manageAgent.id]: nicknameInput.trim() }))
      toast.success(t('settings:agents.nickname.savedToast', { defaultValue: 'Nickname saved' }))
      setManageAgent(null)
    } catch {
      toast.error(t('settings:agents.nickname.saveError', { defaultValue: 'Failed to save nickname' }))
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="size-8 animate-spin text-muted-foreground" /></div>
  }

  const provisionedAgents = agents.filter((a) => a.isProvisioned)
  const unprovisionedAgents = agents.filter((a) => !a.isProvisioned)

  return (
    <>
      <div className="space-y-6">
        {/* Provisioned agents — 2-column card grid */}
        {provisionedAgents.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            {provisionedAgents.map((agent) => {
              const isToggling = togglingAgents.has(agent.id)
              const nickname = nicknamesByAgentId[agent.id]
              return (
                <div
                  key={agent.id}
                  className={`flex flex-col rounded-lg border p-5 transition-all ${
                    agent.isEnabled
                      ? 'bg-white dark:bg-dark-card border-[#e5e5e5] dark:border-dark-border shadow-sm'
                      : 'bg-gray-50 dark:bg-dark-muted border-[#e5e5e5] dark:border-dark-border'
                  }`}
                >
                  {/* Top row: avatar + info + toggle */}
                  <div className="flex items-start gap-3">
                    <div className="size-12 shrink-0 rounded-full overflow-hidden bg-gray-100 dark:bg-dark-accent">
                      <img src={agent.image} alt={agent.name} className="size-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {agent.name}
                        {nickname && (
                          <span className="font-normal text-muted-foreground">
                            {' '}(a.k.a. <span className="text-[#0e7490] font-medium">{nickname}</span>)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{agent.description || agent.title}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isToggling && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                      {agent.alwaysOn ? (
                        <IntuiBadge variant="outline">
                          {t('common:settings.agent_management.always_on', 'Always On')}
                        </IntuiBadge>
                      ) : isAdmin ? (
                        <Switch
                          checked={agent.isEnabled}
                          onCheckedChange={(checked) => handleToggle(agent.id, checked)}
                          disabled={isToggling}
                        />
                      ) : (
                        <IntuiBadge variant={agent.isEnabled ? 'success' : 'secondary'}>
                          {agent.isEnabled
                            ? t('common:settings.agent_management.enabled', 'Enabled')
                            : t('common:settings.agent_management.disabled', 'Disabled')}
                        </IntuiBadge>
                      )}
                    </div>
                  </div>
                  {/* Bottom row: badge + manage */}
                  <div className="flex items-center justify-between mt-4">
                    <IntuiBadge variant={agent.isEnabled ? 'success' : 'secondary'}>
                      {agent.isEnabled
                        ? t('common:settings.agent_management.active', 'Available')
                        : t('common:settings.agent_management.disabled', 'Unavailable')}
                    </IntuiBadge>
                    <IntuiButton variant="outline" size="sm" leftIcon={<Users />} onClick={() => handleManageOpen(agent)}>
                      {t('settings:agents.manage', { defaultValue: 'Manage' })}
                    </IntuiButton>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Unprovisioned agents — dashed border, grayed out */}
        {unprovisionedAgents.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              {t('common:settings.agent_management.not_available', 'Not Available')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {unprovisionedAgents.map((agent) => (
                <div
                  key={agent.id}
                  className="flex items-start gap-3 rounded-lg border border-dashed border-[#e5e5e5] dark:border-dark-border bg-gray-50 dark:bg-dark-muted p-5 opacity-60"
                >
                  <div className="size-12 shrink-0 rounded-full overflow-hidden bg-gray-100 dark:bg-dark-accent grayscale">
                    <img src={agent.image} alt={agent.name} className="size-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-muted-foreground truncate">{agent.name}</p>
                    <p className="text-xs text-muted-foreground/70 truncate mt-0.5">{agent.description}</p>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Lock className="size-3" />
                      <span>{t('common:settings.agent_management.contact_support', 'Contact support to enable')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Non-admin info banner */}
        {!isAdmin && (
          <div className="rounded-lg border border-[#0e7490]/30 bg-[#0e7490]/5 p-4">
            <p className="text-sm text-[#0e7490]">
              {t('common:settings.agent_management.admin_only', 'Only admins can manage agent availability. Contact your administrator for changes.')}
            </p>
          </div>
        )}
      </div>

      {/* Manage (Nickname) Dialog */}
      {manageAgent && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50" onClick={() => setManageAgent(null)}>
          <div className="w-[420px] rounded-xl border border-[#e5e5e5] bg-white p-6 shadow-lg dark:border-dark-border dark:bg-dark-card" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-end justify-end mb-4">
              <button type="button" onClick={() => setManageAgent(null)}><X className="size-4 text-muted-foreground" /></button>
            </div>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('settings:agents.nickname.originalName', { defaultValue: 'Original Name' })}</label>
                <IntuiInput value={manageAgent.name} disabled className="h-9 bg-muted" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium flex items-center gap-1">
                  {t('settings:agents.nickname.label', { defaultValue: 'Nickname' })} <span className="text-muted-foreground text-xs">ⓘ</span>
                </label>
                <IntuiInput
                  placeholder={t('settings:agents.nickname.placeholder', { defaultValue: 'e.g. Sparky, Bot, Max' })}
                  value={nicknameInput}
                  onChange={(e) => setNicknameInput(e.target.value.slice(0, 20))}
                  className="h-9"
                  maxLength={20}
                />
                <p className="text-xs text-muted-foreground text-right">{nicknameInput.length}/20</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-6">
              <IntuiButton variant="outline" onClick={() => setManageAgent(null)}>{t('settings:agents.nickname.cancel', { defaultValue: 'Cancel' })}</IntuiButton>
              <IntuiButton onClick={handleSaveNickname}>{t('settings:agents.nickname.save', { defaultValue: 'Save' })}</IntuiButton>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
